import { DeepPartial } from 'redux';
import { FieldInstance, FieldResult } from 'types/types';
import {
  AssessmentResults,
  AssetAction,
  AssetControl,
  CutdownAssessmentResults,
} from './typesAse';

export enum AssetsSort {
  name = 'name',
  assessmentdue = 'assessmentdue',
}

export enum AssetsOrder {
  asc = 'asc',
  desc = 'desc',
}

export enum AssetsSortOrder {
  NAMEASC = 'name:asc',
  NAMEDESC = 'name:desc',
  ASSESSMENTDUEASC = 'assessmentdue:asc',
}

type Details = {
  name: string;
  custom_reference?: string;
  parentName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  county?: string;
  postcode?: string;
  phone?: string;
  avatar?: string;
};

export enum FieldTypes {
  TEXT = 'Text',
  NUMBER = 'Number',
  TIME = 'Time',
  DATE = 'Date',
  OPTION = 'Option',
  IMAGE = 'Image',
  FILE = 'File',
  NOTE = 'Note',
  PIN = 'Pin',
  QR = 'QR',
  GROUP = 'Group',
  GROUPLIGHT = 'GroupLight',
  // NONE,
}

export type File = {
  id: string;
  type: string;
  name: string;
  timestamp: number;
  editable: boolean;
  deletable: boolean;
};

type AssessmentList = {
  compliance_subtype_id: number;
  status_id: string;
};

type AssessmentDue = {
  compliance_subtype_id: number;
  due_date: string;
};

type AssessmentSummary = {
  list: AssessmentList[];
  due: AssessmentDue[];
};

export interface AssetsFilter {
  search: string;
  organisations: string[];
  assessment_statuses: string[];
}

export type ComplianceSummary = {
  due_date: string | null;
  status_id: string;
  assigned: boolean;
  description: string;
};

export type AssetPlan = {
  id: string;
  fileId: string;
  filePage?: number;
  name: string;
  pinCount: number;
} & (
  | { fileType?: 'image'; fileWidth?: number; fileHeight?: number }
  | {
      fileType?: 'svg' | 'pdf' | 'svgpdf' | 'pdfpage';
      fileWidth: number;
      fileHeight: number;
    }
);

export type PartialAssetsFilters = Partial<AssetsFilter>;

export interface AbridgedAsset {
  // Data Version File
  dv?: Partial<{
    details: number;
    fields: number;
    files: number;
    assessments: number;
    plans: number;
  }>;
  // Data Version List
  dvl?: Partial<{
    details: number;
    fields: number;
    files: number;
    assessments: number;
    plans: number;
  }>;

  id: string;
  reference: string;
  organisation_id: string;
  details: Details;
  assessment_summary: AssessmentSummary;
  compliance_summary: Record<string, ComplianceSummary>;
  downloadFiles: string[];
  downloadThumbnails: string[];
}

export type PartialAbridgedAsset = DeepPartial<AbridgedAsset>;

export interface Asset extends Omit<AbridgedAsset, 'dvl'> {
  // @added 9.3 (2024-10-30) so may be undefined
  assessmentTypesVisible: null | undefined | string[];

  assessment_risk_ratings: Record<string, [string, string, string]>;
  assessment_expiry_timescales: Record<string, [number, number]>;
  assessment_answers: Record<string, string> | undefined;
  team_responsibles: Record<string, string[]>;

  fieldResults: Record<string, FieldResult>;
  fieldInstances: Record<string, FieldInstance>;
  fieldInstanceAutoIds: Record<string, number>;
  // @added 6.28 (2023-06-15) could not upgrade local data
  defaultInstanceChecks?: Record<
    string,
    Record<
      string,
      {
        id: string;
        lastCompleted: null;
        lastAttempted: null;
        overdue: false;
        schedule: {
          due: {
            from: number;
            to: number;
          };
          statusId: null;
        };
      }
    >
  >;
  files: Record<string, File | null>;
  assessments: Record<
    string,
    CutdownAssessmentResults | AssessmentResults | null
  >;
  controls: Record<string, AssetControl>;
  actions: Record<string, AssetAction>;
  plans: Record<string, AssetPlan | null>;
  fieldRepeatableAutoIds?: Record<string, number>;
}

export type AssetFromSync = {
  id: string;
  reference: string;
  organisation_id: string;
} & Partial<Omit<Asset, 'id' | 'reference' | 'organisation_id' | 'dvl'>>;

export interface AssetsState {
  assets_list: Record<string, AbridgedAsset>;
  loaded: Record<string, Asset>;
  // Counts the number of times an asset has been loaded
  loadedCount: Record<string, number>;
  filter: AssetsFilter;
  sort: AssetsSortOrder;
  chosen: string[];
  files: Record<string, string>;
}
